import { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'
import { useFieldArray, useForm, useFormState } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { DEFAULT_VALUES_ADD_RECIPE_THIRD_STEP } from '../../utils/constants'
import routes, { groupRecipeLink } from '../../routers'
import useDirty from '../../utils/hooks/useDirty'
import { setLoading } from '../../redux/actions/ui'
import { createRecipe, getRecipeById } from '../../api/recipe'
import ButtonCustom from '../../components/ButtonCustom'
import RecipeSteps from '../../components/RecipeSteps'
import FieldFeedback from '../../components/FieldFeedback'
import { scrollToFirstProblem, showServerErrors } from '../../utils/recipeLimits'
import LayoutRecipe from './components/LayoutRecipe'

import './style.css'

const AddRecipe = ({ myGroups, user }) => {
  const [required, setRequired] = useState(true)
  const [disableButton, setDisableButton] = useState(false)
  const navigation = useNavigate()
  const dispatch = useDispatch()
  const { control, handleSubmit, reset, setError } = useForm()
  // Problems the API found that aren't next to a field on this page (for example a
  // summary still missing on the first page when publishing)
  const [problems, setProblems] = useState([])
  const [draftSaved, setDraftSaved] = useState(false)
  const { dirtyFields, errors } = useFormState({ control })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'steps',
  })
  const { filterDirtyFields } = useDirty()
  const [idRecipe] = useSearchParams({})
  const idRecipeForm = idRecipe.get('id')
  const step = fields?.length + 1

  const addNewIngredient = () => {
    append({
      description: '',
      step,
    })
  }

  const onSubmit = (data) => {
    const steps = data?.steps?.map((item, index) => ({ ...item, step: index + 1 }))
    const newData = { steps, id: idRecipeForm, to_publish: required, current_step: 3 }

    setDisableButton(true)
    setProblems([])
    createRecipe(newData)
      .then(({ data }) => {
        if (required) {
          // Added for a family member: show it where the family sees it, under their name
          const sharedGroup =
            data?.user_id !== user?.id && myGroups?.find(({ id }) => data?.groups?.includes(id))
          if (sharedGroup) {
            return navigation(groupRecipeLink(sharedGroup, data))
          }
          // The profile shows how close the family is to a printed cookbook
          return navigation(routes.userProfile, { state: { savedRecipe: idRecipeForm } })
        }
        setDraftSaved(true)
      })
      .catch((e) => {
        setProblems(showServerErrors(e, setError, 3))
        scrollToFirstProblem()
      })
      .finally(() => setDisableButton(false))
  }

  useEffect(() => {
    if (!isEmpty(idRecipeForm)) {
      dispatch(setLoading(true))
      return getRecipeById(idRecipeForm)
        .then(({ data }) => {
          const filterDataRecipe = filterDirtyFields({
            dirtyFields: DEFAULT_VALUES_ADD_RECIPE_THIRD_STEP,
            data,
            checkArray: true,
          })

          return reset(
            { ...DEFAULT_VALUES_ADD_RECIPE_THIRD_STEP, ...filterDataRecipe },
            { keepDefaultValues: Object.keys(filterDataRecipe).length === 1 }
          )
        })
        .catch(() => navigation(routes.notFound))
        .finally(() => dispatch(setLoading(false)))
    }
    navigation(routes.notFound)
  }, [])

  return (
    <LayoutRecipe
      step={3}
      navigationNext
      isLoading={disableButton}
      setRequired={setRequired}
      navigationPrevTitle="Publish recipe"
      title="How do you make it?"
      subTitle="Write it step by step, the way you would tell someone in your kitchen."
      disableButtonSave={disableButton}
      onSubmit={handleSubmit(onSubmit, scrollToFirstProblem)}
      problems={problems}
      draftSaved={draftSaved}
      onDraftSavedClose={() => setDraftSaved(false)}
      navigationPrev={`${routes.addRecipeSecond}?id=${idRecipeForm}`}
    >
      <section className="formSection">
        <h2 className="formSectionTitle">Steps</h2>
        <p className="formSectionHint">One step per box. Line breaks are kept.</p>
        <div className="innerAllSteps">
          {fields.map((item, index) => {
            return (
              <RecipeSteps
                key={item.id}
                index={index}
                control={control}
                dirtyFields={dirtyFields}
                remove={remove}
              />
            )
          })}
        </div>
        {/* "Please add at least one step" from the API is about the whole list */}
        <FieldFeedback error={errors.steps?.message ? errors.steps : null} />
        <div className="recipeButtonWrap">
          <ButtonCustom
            onClick={() => addNewIngredient()}
            type="button"
            style="recipeButtonUpload"
            title="+ Add another step"
          />
        </div>
      </section>
    </LayoutRecipe>
  )
}

export default connect(({ groups, user }) => ({
  myGroups: groups.data,
  user: user.data,
}))(AddRecipe)
